import { useQueryClient, QueryKey } from "@tanstack/react-query";
import { useEffect, useState, useMemo, useCallback } from "react";

/**
 * Custom hook to observe changes in the React Query cache
 *
 * @param queryKey - The query key to observe
 * @returns The current cache data for the specified key
 */
export function useQueryCache<T>(queryKey: unknown[]) {
  const queryClient = useQueryClient();
  const [data, setData] = useState<T | undefined>();

  useEffect(() => {
    // Get initial data
    const initialData = queryClient.getQueryData(queryKey) as T | undefined;
    setData(initialData);

    // Subscribe to cache changes
    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      const updatedData = queryClient.getQueryData(queryKey) as T | undefined;
      setData(updatedData);
    });

    // Clean up subscription
    return () => {
      unsubscribe();
    };
  }, [queryClient, ...queryKey]);

  return data;
}

type Updater<T> = T | ((old: T | undefined) => T);

/**
 * Custom hook to observe changes in the React Query cache
 * and refresh data
 *
 * @param queryKey - The query key to observe
 * @returns The current cache data for the specified key
 */
export function useQueryCacheManager<T>(queryKey: QueryKey) {
  const UPDATE_EVENTS = new Set(["updated", "observerResultsUpdated"] as const);
  const qc = useQueryClient();
  const hash = useMemo(() => JSON.stringify(queryKey), [queryKey]);

  const [data, setData] = useState<T | undefined>(() => {
    return qc.getQueryData(queryKey) as T | undefined;
  });

  useEffect(() => {
    const unsub = qc.getQueryCache().subscribe((event: any) => {
      // Only react to relevant updates for THIS key
      if (!event?.query) return;
      if (!UPDATE_EVENTS.has(event.type)) return;
      if (JSON.stringify(event.query.queryKey) !== hash) return;
      setData(qc.getQueryData(queryKey) as T | undefined);
    });

    // Sync once on key change
    setData(qc.getQueryData(queryKey) as T | undefined);

    return () => unsub();
  }, [qc, hash]);

  const invalidate = useCallback(
    () => qc.invalidateQueries({ queryKey }),
    [qc, hash],
  );

  const refetch = useCallback(
    () => qc.refetchQueries({ queryKey, type: "active" }),
    [qc, hash],
  );

  const set = useCallback(
    (updater: Updater<T>) =>
      qc.setQueryData(queryKey, (old: T | undefined) =>
        typeof updater === "function" ? (updater as any)(old) : updater,
      ),
    [qc, hash],
  );

  // Convenience: invalidate then refetch; awaits both
  const refresh = useCallback(async () => {
    await invalidate();
    await refetch();
  }, [invalidate, refetch]);

  return { data, refresh, invalidate, refetch, set };
}

/**
 * Custom hook to observe the loading state of a query
 *
 * @param queryKey - The query key to observe
 * @returns The current loading state for the specified query
 */
export function useQueryLoading(queryKey: unknown[]): boolean {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const query = queryClient.getQueryCache().find({ queryKey });
    setIsLoading(query?.state.status === "pending");

    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (
        event?.query.queryHash ===
        queryClient.getQueryCache().find({ queryKey })?.queryHash
      ) {
        setIsLoading(event.query.state.status === "pending");
      }
    });

    return () => {
      unsubscribe();
    };
  }, [queryClient, ...queryKey]);

  return isLoading;
}
